import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface StatusCustomizado {
  id: string;
  nome: string;
  descricao?: string;
  setor: 'projeto' | 'orcamento' | 'pedido' | 'contrato' | 'financeiro' | 'sinistro' | 'servico';
  cor?: string;
  ordem?: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useStatusCustomizados() {
  return useQuery({
    queryKey: ['status-customizados'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('status_customizados')
        .select('*')
        .eq('ativo', true)
        .order('setor')
        .order('ordem');

      if (error) {
        console.error('Erro ao buscar status customizados:', error);
        throw error;
      }

      return data as StatusCustomizado[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useStatusCustomizadosPorSetor(setor: StatusCustomizado['setor']) {
  return useQuery({
    queryKey: ['status-customizados', setor],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('status_customizados')
        .select('*')
        .eq('setor', setor)
        .eq('ativo', true)
        .order('ordem');

      if (error) {
        console.error('Erro ao buscar status customizados:', error);
        throw error;
      }

      return data as StatusCustomizado[];
    },
    enabled: !!setor,
  });
}

export function useCriarStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (statusData: {
      nome: string;
      descricao?: string;
      setor: 'projeto' | 'orcamento' | 'pedido' | 'contrato' | 'financeiro' | 'sinistro' | 'servico';
      cor?: string;
      ordem?: number;
    }) => {
      const { data, error } = await supabase
        .from('status_customizados')
        .insert(statusData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-customizados'] });
    }
  });
}

export function useAtualizarStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: string; 
      nome?: string;
      descricao?: string;
      setor?: 'projeto' | 'orcamento' | 'pedido' | 'contrato' | 'financeiro' | 'sinistro' | 'servico';
      cor?: string;
      ordem?: number;
      ativo?: boolean;
    }) => {
      const { data, error } = await supabase
        .from('status_customizados')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['status-customizados'] });
    }
  });
}